import { useCallback } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import { useTenantCode } from './useTenantCode';

/**
 * `navigate` que antepone el tenantCode a las rutas absolutas.
 *
 * Casi toda la aplicación vive bajo `/:tenantCode/*`, así que un
 * `navigate('/clientes')` no lleva al listado: lleva a `/clientes`, que casa con
 * `/:tenantCode/*` tomando "clientes" como tenantCode. A partir de ahí el
 * interceptor de axios lee el primer segmento de la URL y firma cada petición
 * con el tenant equivocado — de ahí la ráfaga de 403 en `/api/clientes/...` y el
 * 404 del branding.
 *
 * Las rutas que sí son globales (`/login`, `/admin/...`, `/firmar/...`) siguen
 * usando `useNavigate` directamente.
 */
export const useTenantNavigate = () => {
    const navigate = useNavigate();
    const tenantCode = useTenantCode();

    return useCallback(
        (to: string, options?: NavigateOptions) => {
            // Sin tenant (superadmin) o ruta relativa: se deja tal cual. El
            // segundo caso evita doblar el prefijo si quien llama ya lo puso.
            const needsPrefix =
                !!tenantCode &&
                to.startsWith('/') &&
                to !== `/${tenantCode}` &&
                !to.startsWith(`/${tenantCode}/`);

            navigate(needsPrefix ? `/${tenantCode}${to}` : to, options);
        },
        [navigate, tenantCode]
    );
};
